import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/AuthContext";
import { LOGIN } from "@/constants/testIds/auth";
import { ADMIN } from "@/constants/testIds";
import Seo from "@/components/Seo";

export default function AdminLogin() {
    const { user, login, loading } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!loading && user) navigate("/admin/dashboard", { replace: true });
    }, [loading, user, navigate]);

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        const res = await login(email.trim(), password);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Welcome back, admin.");
            navigate("/admin/dashboard", { replace: true });
        } else {
            toast.error(res.error);
        }
    }

    return (
        <div data-testid={ADMIN.loginRoot} className="min-h-screen bg-navy-900 relative overflow-hidden flex items-center justify-center p-4">
            <Seo title="Admin · Sign in" description="ClaimHub UK admin sign in." path="/admin/login" noIndex />
            <div className="absolute inset-0 bg-mesh-emerald" />
            <div className="absolute inset-0 grid-bg-dark opacity-30" />

            <div className="relative w-full max-w-md">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-emerald-300 hover:text-emerald-200 mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to ClaimHub UK
                </Link>

                <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10">
                    <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-xl bg-navy-900 flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="font-display font-bold text-navy-900 text-lg">ClaimHub <span className="text-emerald-600">UK</span></div>
                    </div>

                    <h1 className="mt-6 text-3xl font-display font-bold tracking-tight text-navy-900">Admin sign in</h1>
                    <p className="mt-2 text-sm text-slate-500">Authorised personnel only.</p>

                    <form onSubmit={submit} className="mt-7 space-y-4">
                        <div>
                            <Label className="text-sm font-semibold text-navy-900">Email</Label>
                            <Input
                                data-testid={LOGIN.emailInput}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="admin@claimhubuk.co.uk"
                                className="mt-2 h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <Label className="text-sm font-semibold text-navy-900">Password</Label>
                            <Input
                                data-testid={LOGIN.passwordInput}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="••••••••"
                                className="mt-2 h-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid={LOGIN.submitButton}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : "Sign in"}
                        </button>
                    </form>

                    <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500 leading-relaxed">
                        Trouble signing in? Contact <a className="text-emerald-700" href="mailto:tech@claimhubuk.co.uk">tech@claimhubuk.co.uk</a>.
                    </div>
                </div>
            </div>
        </div>
    );
}
